/**
 *
 * Copyright (c) 2019-present, wobe-systems GmbH
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * to be found in the LICENSE file in the root directory of this source tree.
 */

import Vue from 'vue';
import VueRouter from 'vue-router';
import * as types from '@/store/types';
import store from '@/store/index';
import FlowList from '../views/FlowList.vue';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('../views/Workspaces.vue'),
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: '/apis/:workspaceId',
    name: 'APIs',
    component: () => import('../views/API.vue'),
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: '/flows/:workspaceId',
    name: 'flows',
    component: FlowList,
    props: true,
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: '/about',
    name: 'about',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "about" */ '../views/About.vue'),
    meta: {
      requiresAuth: false,
    },
  },

  {
    path: '/editor/:workspaceId/:flowId',
    name: 'flow',
    component: () => import(/* webpackChunkName: "flows" */ '../views/Editor.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: '/bricks/:workspaceId',
    name: 'bricks',
    props: true,
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "bricks" */ '../views/BrickList.vue'),
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: '/packages/:workspaceId',
    name: 'packages',
    props: true,
    component: () => import(/* webpackChunkName: "bricks" */ '../views/Packages.vue'),
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: '/signup',
    name: 'signup',
    component: () => import(/* webpackChunkName: "bricks" */ '../views/Signup.vue'),
    meta: {
      requiresAuth: false,
    },
  },

  {
    path: '/forgetPassword',
    name: 'forgetPassword',
    props: true,
    component: () => import(/* webpackChunkName: "bricks" */ '../views/ForgetPassword.vue'),
    meta: {
      requiresAuth: false,
    },
  },

  {
    path: '/login',
    name: 'login',
    component: () => import('../views/Login.vue'),
    meta: {
      requiresAuth: false,
    },
  },

  {
    path: '/resetPassword',
    name: 'resetPassword',
    component: () => import('../views/ResetPassword.vue'),
    meta: {
      requiresAuth: false,
    },
  },

  {
    path: '/logging',
    name: 'Logging',
    component: () => import('../views/Logging.vue'),
    meta: {
      requiresAuth: false,
    },
  },

  {
    path: '/userAccount',
    name: 'userAccount',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "bricks" */ '../views/UserAccount.vue'),
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: '/404Page',
    name: '404Page',
    component: () => import('../views/404.vue'),
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: '/adminPanel',
    name: 'AdminPanel',
    component: () => import('../views/AdminPanel.vue'),
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: '/signuprequest',
    name: 'SignupRequest',
    async beforeEnter(to: any, from: any, next: any) {
      await store.dispatch(`platformConfig/${types.FETCH_PLATFORM_CONFIG}`);
      if (!store.getters[`platformConfig/${types.GET_PLATFORM_CONFIG}`].enableSignup) {
        if (to.query.id) {
          next();
        } else {
          next({
            path: '/login',
          });
        }
      } else {
        next();
      }
    },
    component: () => import('../views/SignupRequest.vue'),
    meta: {
      requiresAuth: false,
    },
  },

  {
    path: '/changeemail/:token',
    name: 'ChangeEmail',
    props: true,
    component: () => import('../views/ChangeEmail.vue'),
    meta: {
      public: true,
    },
  },

  {
    path: '/workspacesetting/:workspaceId',
    name: 'WorkspaceSetting',
    props: true,
    component: () => import('../views/WorkspaceSetting.vue'),
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: '/typeslibrary/:workspaceId',
    name: 'TypesLibrary',
    props: true,
    component: () => import('../views/TypesLibrary.vue'),
    meta: {
      requiresAuth: true,
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(async (to, from, next) => {
  // If the route is not available redirect the user to 404 page
  if (!to.name) {
    next({
      path: '/404Page',
      replace: true,
    });
  } else {
    next();
  }

  // if page has public in metadata, dosn't matter if logged in or not
  if (to && to.meta && to.meta.public) {
    next();
    return;
  }

  const needToSkipAuth = (toRoute: any) => !toRoute.meta.requiresAuth;

  // Check if use is authenticated
  const userId = store.getters[`user/${types.GET_USER}`].id;

  if (userId) {
    next();
  } else {
    // try to fetch user in case page is refreshed and user is not yet in store
    await store.dispatch(`user/${types.FETCH_LOGGED_IN_USER}`);
    const getUser = store.getters[`user/${types.GET_USER}`].id;
    if (getUser) {
      next();
    } else if (needToSkipAuth(to)) {
      next();
    } else {
      router.push('/login');
    }
  }
  // Don't let user enter to signin and login if already logedin
  if (store.getters[`user/${types.GET_USER}`].id) {
    if (needToSkipAuth(to)) {
      router.push(from.path);
    }
  } else {
    next();
  }
});

export default router;
